export enum ClientFacingErrors {
  /**
   * System
   */
  RateLimit = "RATE_LIMIT",
  Unexpected = "UNEXPECTED",
  NotFound = "NOT_FOUND",
  /** The operation exists in the schema but is not built yet. */
  NotImplemented = "NOT_IMPLEMENTED",
  /** An outcomes request refused in words an admin can act on; the page shows the message. */
  OutcomeRequestRefused = "OUTCOME_REQUEST_REFUSED",
  LeadScoringModelInvalid = "LEAD_SCORING_MODEL_INVALID",
  LeadScoringModelConflict = "LEAD_SCORING_MODEL_CONFLICT",
  LeadScoringConfigInvalid = "LEAD_SCORING_CONFIG_INVALID",
  LeadScoringConfigConflict = "LEAD_SCORING_CONFIG_CONFLICT",

  /**
   * Auth
   */
  NotAuthorized = "NOT_AUTHORIZED",
  InvalidAccessToken = "INVALID_ACCESS_TOKEN",
  InvalidRefreshToken = "INVALID_REFRESH_TOKEN",
  AccountNotAllowed = "ACCOUNT_NOT_ALLOWED",
  GoogleAuthFailed = "GOOGLE_AUTH_FAILED",
  AppleAuthFailed = "APPLE_AUTH_FAILED",
  InvalidUser = "INVALID_USER",
  InvalidPhoneNumber = "INVALID_PHONE_NUMBER",
  InvalidEmailAddress = "INVALID_EMAIL_ADDRESS",
  InvalidVerifyCode = "INVALID_VERIFY_CODE",
  SmsNotSupported = "SMS_NOT_SUPPORTED",
  MaxSendAttempts = "MAX_SEND_ATTEMPTS",
  UserWithPhoneAlreadyExists = "USER_WITH_PHONE_ALREADY_EXISTS",
  UserWithEmailAlreadyExists = "USER_WITH_EMAIL_ALREADY_EXISTS",
  UserIsSharedGoogleAccount = "USER_IS_SHARED_GOOGLE_ACCOUNT",
  GoogleSignInForNewUser = "GOOGLE_SIGN_IN_FOR_NEW_USER",
  AppleSignInForNewUser = "APPLE_SIGN_IN_FOR_NEW_USER",
  SessionNotFound = "SESSION_NOT_FOUND",

  /**
   * AI
   */
  AudioTranscriptionFailed = "AUDIO_TRANSCRIPTION_FAILED",
  CoachingSummaryFailed = "COACHING_SUMMARY_FAILED",

  /**
   * Customer
   */
  CustomerDoesNotExist = "CUSTOMER_DOES_NOT_EXIST",
  CustomerNeedsAtLeastOneFormOfAuth = "CUSTOMER_NEEDS_AT_LEAST_ONE_FORM_OF_AUTH",

  /**
   * EnrollmentItem
   */
  CurrentEnrollmentItemForProgramAlreadyExists = "ENROLLMENT_ITEM_FOR_PROGRAM_ALREADY_EXISTS",
  OnlyCreateByCustomerAllowed = "ONLY_CREATE_BY_CUSTOMER_ALLOWED",
  CustomerZipCodeRestricted = "CUSTOMER_ZIP_CODE_RESTRICTED",
  SchoolCannotRejectEnrollmentItem = "SCHOOL_CANNOT_REJECT_ENROLLMENT_ITEM",
  EnrollmentItemNotEligibleForLead = "ENROLLMENT_ITEM_NOT_ELIGIBLE_FOR_LEAD",
  /** A write into a deprecated status: the retired school-approval funnel or CLOSED_LOST (ENG-7335). */
  EnrollmentItemStatusDeprecated = "ENROLLMENT_ITEM_STATUS_DEPRECATED",
  /** A LEAD change refused because the program is paused or at its campaign cap.
   *  Its own code because `debugInfo` is stripped for non-admins in production. */
  ProgramSuppressedForLead = "PROGRAM_SUPPRESSED_FOR_LEAD",
  EnrollmentItemNotRejectedForLeadAcceptance = "ENROLLMENT_ITEM_NOT_REJECTED_FOR_LEAD_ACCEPTANCE",
  RejectedLeadAcceptanceBlockedByCurrentEnrollment = "REJECTED_LEAD_ACCEPTANCE_BLOCKED_BY_CURRENT_ENROLLMENT",
  RejectedLeadAcceptanceLedgerInconsistent = "REJECTED_LEAD_ACCEPTANCE_LEDGER_INCONSISTENT",
  RejectedLeadAcceptanceNotBillable = "REJECTED_LEAD_ACCEPTANCE_NOT_BILLABLE",

  /**
   * Clickout enrollment backfill (admin historical import / correction)
   */
  ClickoutBackfillCustomerNotFound = "CLICKOUT_BACKFILL_CUSTOMER_NOT_FOUND",
  ClickoutBackfillEnrollmentAlreadyExists = "CLICKOUT_BACKFILL_ENROLLMENT_ALREADY_EXISTS",
  ClickoutBackfillEnrollmentNotFound = "CLICKOUT_BACKFILL_ENROLLMENT_NOT_FOUND",
  ClickoutBackfillNotAClickoutEnrollment = "CLICKOUT_BACKFILL_NOT_A_CLICKOUT_ENROLLMENT",
  ClickoutBackfillInvalidLeadDate = "CLICKOUT_BACKFILL_INVALID_LEAD_DATE",
  ClickoutBackfillInvalidRevenueOverride = "CLICKOUT_BACKFILL_INVALID_REVENUE_OVERRIDE",
  ClickoutBackfillInvalidInput = "CLICKOUT_BACKFILL_INVALID_INPUT",
  ClickoutBackfillMarketplaceEventNotFound = "CLICKOUT_BACKFILL_MARKETPLACE_EVENT_NOT_FOUND",
  ClickoutBackfillMarketplaceEventHasNoCustomer = "CLICKOUT_BACKFILL_MARKETPLACE_EVENT_HAS_NO_CUSTOMER",

  /**
   * Host & Post enrollment correction (admin re-dating / re-pricing of an existing hosted lead)
   */
  HostAndPostCorrectionEnrollmentNotFound = "HOST_AND_POST_CORRECTION_ENROLLMENT_NOT_FOUND",
  /** Not a billed SCHOOL lead that Dreambound hosted (clickouts, non-LEAD statuses, never-billed leads). */
  HostAndPostCorrectionNotEligible = "HOST_AND_POST_CORRECTION_NOT_ELIGIBLE",
  /** Nothing to correct: no lead date, revenue date, or revenue amount was supplied. */
  HostAndPostCorrectionInvalidInput = "HOST_AND_POST_CORRECTION_INVALID_INPUT",
  HostAndPostCorrectionInvalidDate = "HOST_AND_POST_CORRECTION_INVALID_DATE",
  HostAndPostCorrectionInvalidRevenueOverride = "HOST_AND_POST_CORRECTION_INVALID_REVENUE_OVERRIDE",
  /** The lead's revenue/invoice history is not a shape a correction can safely reverse. */
  HostAndPostCorrectionLedgerInconsistent = "HOST_AND_POST_CORRECTION_LEDGER_INCONSISTENT",

  /**
   * LeadDeliveryMapping
   */
  LeadDeliveryMappingAlreadyExists = "LEAD_DELIVERY_MAPPING_ALREADY_EXISTS",

  /**
   * Campaign
   */
  /**
   * A campaign scope row named zero tiers or more than one. Mirrors the
   * `campaign_scope_exactly_one_tier` CHECK, raised before the write so the
   * campaign form can point at the offending row instead of surfacing a
   * constraint violation.
   */
  CampaignScopeInvalidTier = "CAMPAIGN_SCOPE_INVALID_TIER",
  /**
   * A budget amount that is not a non-negative whole number. Empty is valid and
   * means uncapped, which is why this cannot simply coerce.
   */
  CampaignBudgetAmountInvalid = "CAMPAIGN_BUDGET_AMOUNT_INVALID",
  CampaignCapWarningThresholdInvalid = "CAMPAIGN_CAP_WARNING_THRESHOLD_INVALID",
  CampaignBudgetUnitInvalid = "CAMPAIGN_BUDGET_UNIT_INVALID",
  /** A different allocation revision was saved while this editor was open. */
  CampaignBudgetConflict = "CAMPAIGN_BUDGET_CONFLICT",
  /** The campaign moved into a different month while this editor was open. */
  CampaignBudgetPeriodConflict = "CAMPAIGN_BUDGET_PERIOD_CONFLICT",
  /** A cap citing an external source with no usable link. */
  CampaignBudgetEvidenceUrlRequired = "CAMPAIGN_BUDGET_EVIDENCE_URL_REQUIRED",
  /** A cap source outside the allowlist, or an attempt to clear one. */
  CampaignBudgetSourceInvalid = "CAMPAIGN_BUDGET_SOURCE_INVALID",

  /**
   * A campaign name that is empty once trimmed. `Campaign.jsonSchema` has no
   * `minLength` and the column is `notNullable()`, which `""` satisfies, so
   * nothing below this would reject it — and since campaign names are
   * deliberately not unique, the name is the only human-facing handle a
   * campaign has.
   */
  CampaignNameRequired = "CAMPAIGN_NAME_REQUIRED",
  CampaignCapAlertRecipientsInvalid = "CAMPAIGN_CAP_ALERT_RECIPIENTS_INVALID",

  /**
   * A client supplied something that is not an IANA zone where a campaign
   * timezone was expected. The `Timezone` scalar only asserts "is a string", so
   * a near-miss like "Americas/New_York" would otherwise reach the month math —
   * on the write path via `Campaign.$validateTimezone`, which throws a bare
   * `Error`, and on the scope preview, which never touches the model at all.
   * Raised ahead of both so the form gets a field to point at rather than a
   * server error.
   */
  CampaignInvalidTimezone = "CAMPAIGN_INVALID_TIMEZONE",
  CampaignReactivationTimeInvalid = "CAMPAIGN_REACTIVATION_TIME_INVALID",

  /**
   * Pause
   */
  PauseInvalidTarget = "PAUSE_INVALID_TARGET",
  PauseAlreadyActive = "PAUSE_ALREADY_ACTIVE",
  /** Malformed resume date, time, timezone or incompatible scheduling arguments. */
  PauseResumeInvalid = "PAUSE_RESUME_INVALID",
  PauseResumeNotAfterStart = "PAUSE_RESUME_NOT_AFTER_START",
  /** An edited resume date has to be after now, so an edit can never end a hold. */
  PauseResumeInPast = "PAUSE_RESUME_IN_PAST",
  /** The hold was ended or ran out, so there is nothing left to edit. */
  PauseNotEditable = "PAUSE_NOT_EDITABLE",

  /**
   * Geocode
   */
  CouldNotFindGeocode = "COULD_NOT_FIND_GEOCODE",
  InvalidAddress = "INVALID_ADDRESS",
  /**
   * Geocoding was temporarily unavailable (e.g. a Google Maps timeout).
   * Distinct from InvalidAddress so a Google outage stays distinguishable
   * from a spike in genuinely bad addresses in error metrics (ENG-7782).
   */
  GeocodeUnavailable = "GEOCODE_UNAVAILABLE",

  /**
   * Provider
   */
  ProviderDeleteNotAllowed = "PROVIDER_DELETE_NOT_ALLOWED",
  ProviderNotPartnered = "PROVIDER_NOT_PARTNERED",

  /**
   * Program
   */
  ProgramDeleteNotAllowed = "PROGRAM_DELETE_NOT_ALLOWED",
  ProgramUpdateNotAllowed = "PROGRAM_UPDATE_NOT_ALLOWED",
  ProgramCannotCollectPaymentDueToRevenueConfiguration = "PROGRAM_CANNOT_COLLECT_PAYMENT_DUE_TO_REVENUE_CONFIGURATION",

  /**
   * User
   */
  UserProviderAlreadyExists = "USER_PROVIDER_ALREADY_EXISTS",
  ContactBelongsToDifferentUser = "CONTACT_BELONGS_TO_DIFFERENT_USER",

  /**
   * Vocation
   */
  VocationDeleteRelatedProgramsExists = "VOCATION_DELETE_RELATED_PROGRAMS_EXISTS",

  /**
   * Misc
   */
  NeedAtLeastOne = "NEED_AT_LEAST_ONE",
}
